import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Icon,
    Label,
    Separator,
    SidebarMenuButton,
    SidebarMenuItem,
    Spinner,
    useSidebar,
} from "@elit-hair/elit-ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbEye } from "react-icons/tb";

import { useTeams } from "@/hooks/queries/team/use-team-queries";
import { cn } from "@/lib/utils";
import { isDevEnvironment } from "@/utils/is-dev-environment";

import { PREVIEWABLE_PERMISSIONS } from "./constants/role-permission-presets";
import { useDashboardRole } from "./hooks/useDashboardRole";
import { DASHBOARD_ROLE_LABEL_KEYS, DASHBOARD_ROLE_LIST } from "./types/dashboard-role.types";

export function RolePreviewSelect() {
    const { t } = useTranslation();
    const { state } = useSidebar();
    const { role, permissionCodes, teamIds, setPreviewRole, togglePermission, toggleTeam, clearPreview, isPreview } =
        useDashboardRole();
    const { data: teamsData, isLoading: isLoadingTeams } = useTeams();
    const [open, setOpen] = useState(false);
    const roleLabel = t(DASHBOARD_ROLE_LABEL_KEYS[role]);
    const permissionSet = useMemo(() => new Set(permissionCodes), [permissionCodes]);
    const teamIdSet = useMemo(() => new Set(teamIds), [teamIds]);
    const sortedPermissions = useMemo(
        () =>
            [...PREVIEWABLE_PERMISSIONS].sort((a, b) => {
                const aSelected = permissionSet.has(a.code);
                const bSelected = permissionSet.has(b.code);
                if (aSelected === bSelected) {
                    return a.name.localeCompare(b.name);
                }
                return aSelected ? -1 : 1;
            }),
        [permissionSet]
    );
    const sortedTeams = useMemo(() => {
        const teams = teamsData?.data ?? [];
        return [...teams].sort((a, b) => {
            const aSelected = teamIdSet.has(a.id);
            const bSelected = teamIdSet.has(b.id);
            if (aSelected === bSelected) {
                return a.name.localeCompare(b.name);
            }
            return aSelected ? -1 : 1;
        });
    }, [teamsData?.data, teamIdSet]);

    if (!isDevEnvironment()) {
        return null;
    }

    return (
        <SidebarMenuItem>
            <SidebarMenuButton
                tooltip={state === "collapsed" ? t("dashboard_role_preview") : undefined}
                aria-label={t("dashboard_role_preview")}
                onClick={() => setOpen(true)}
            >
                <Icon icon={TbEye} />
                <span className="truncate">{roleLabel}</span>
            </SidebarMenuButton>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden border-border/80 bg-card shadow-xl sm:max-w-lg">
                    <DialogHeader className="space-y-1">
                        <DialogTitle>{t("dashboard_role_preview")}</DialogTitle>
                        <DialogDescription>{roleLabel}</DialogDescription>
                    </DialogHeader>

                    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden py-1">
                        <div className="space-y-2">
                            <p className="text-sm font-medium">{t("dashboard_role_presets")}</p>
                            <div className="flex flex-wrap gap-2">
                                {DASHBOARD_ROLE_LIST.map((dashboardRole) => {
                                    const isActive = role === dashboardRole;

                                    return (
                                        <Button
                                            key={dashboardRole}
                                            type="button"
                                            size="sm"
                                            variant={isActive ? "default" : "outline"}
                                            onClick={() => setPreviewRole(dashboardRole)}
                                        >
                                            {t(DASHBOARD_ROLE_LABEL_KEYS[dashboardRole])}
                                        </Button>
                                    );
                                })}
                            </div>
                        </div>

                        <Separator />

                        <div className="flex max-h-40 min-h-0 flex-col gap-2 overflow-hidden">
                            <p className="text-sm font-medium">{t("dashboard_role_teams")}</p>
                            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                                {isLoadingTeams ? (
                                    <div className="flex items-center gap-2 px-2 py-2 text-sm text-muted-foreground">
                                        <Spinner size="sm" />
                                        {t("loading")}
                                    </div>
                                ) : sortedTeams.length === 0 ? (
                                    <p className="px-2 py-2 text-sm text-muted-foreground">{t("no_teams_found")}</p>
                                ) : (
                                    sortedTeams.map((team) => {
                                        const checked = teamIdSet.has(team.id);
                                        const id = `role-preview-team-${team.id}`;

                                        return (
                                            <Label
                                                key={team.id}
                                                htmlFor={id}
                                                className={cn(
                                                    "flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/60",
                                                    checked && "bg-muted/40"
                                                )}
                                            >
                                                <Checkbox
                                                    id={id}
                                                    checked={checked}
                                                    onCheckedChange={() => toggleTeam(team.id)}
                                                />
                                                <span
                                                    className="h-3 w-3 shrink-0 rounded-full"
                                                    style={{ backgroundColor: team.color }}
                                                />
                                                <span className="truncate text-sm font-medium leading-none">
                                                    {team.name}
                                                </span>
                                            </Label>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <Separator />

                        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
                            <p className="text-sm font-medium">{t("dashboard_role_permissions")}</p>
                            <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                                {sortedPermissions.map(({ name, code }) => {
                                    const checked = permissionSet.has(code);
                                    const id = `role-preview-permission-${code}`;

                                    return (
                                        <Label
                                            key={code}
                                            htmlFor={id}
                                            className={cn(
                                                "flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/60",
                                                checked && "bg-muted/40"
                                            )}
                                        >
                                            <Checkbox
                                                id={id}
                                                checked={checked}
                                                onCheckedChange={() => togglePermission(code)}
                                                className="mt-0.5"
                                            />
                                            <span className="flex min-w-0 flex-col gap-0.5">
                                                <span className="text-sm font-medium leading-none">{name}</span>
                                                <span className="text-xs text-muted-foreground">{code}</span>
                                            </span>
                                        </Label>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" disabled={!isPreview} onClick={clearPreview}>
                            {t("dashboard_role_reset")}
                        </Button>
                        <Button type="button" onClick={() => setOpen(false)}>
                            {t("close")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </SidebarMenuItem>
    );
}
